import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
} from "@/components/ui/carousel";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const subCategories = [
    {
        title: "Cooktops and rangehoods",
        image: `${process.env.REACT_APP_BLOB_URL}/www/img/kitchens/fisher-and-paykel-gas-hob.png`,
        alt: "Fisher & Paykel four-burner gas cooktop featuring stainless steel construction, cast iron pan supports and front-mounted controls.",
    },
    {
        title: "Ovens",
        image: `${process.env.REACT_APP_BLOB_URL}/www/img/kitchens/miele-ovens-stainless-steel.png`,
        alt: "Miele integrated cooking appliances featuring a steam oven, built-in coffee machine and multifunction wall oven in a modern kitchen."
    },
    {
        title: "Fridges & freezers",
        image: `${process.env.REACT_APP_BLOB_URL}/www/img/kitchens/panasonic-quad-fridge-stainless-steel.png`,
        alt: "Panasonic stainless steel quad door fridge featuring an integrated water dispenser and contemporary multi-door food storage design."
    },
    {
        title: "Dishwashers & under sink",
        image: `${process.env.REACT_APP_BLOB_URL}/www/img/kitchens/bosch-dishwasher-black.png`,
        alt: "Bosch built-in dishwasher featuring a black stainless steel finish and seamless integration with contemporary kitchen cabinetry."
    },
    {
        title: "Small appliances",
        image: `${process.env.REACT_APP_BLOB_URL}/www/img/kitchens/smeg-white-gloss-small-appliances.png`,
        alt: "SMEG small kitchen appliances including an espresso coffee machine, drip coffee maker, electric kettle and toaster in a contemporary kitchen."
    },
    {
        title: "Kitchen taps and sinks",
        image: `${process.env.REACT_APP_BLOB_URL}/www/img/kitchens/zenith-celcius-gooseneck-tap.png`,
        alt: "Zenith Celsius kitchen tap featuring a brushed stainless steel gooseneck design and integrated sink in a modern kitchen."
    },
    {
        title: "Beverage centres",
        image: `${process.env.REACT_APP_BLOB_URL}/www/img/kitchens/rinnai-beverage-centre-black.png`,
        alt: "Freestanding wine refrigerator with glass display door and timber shelves for hospitality venues, restaurants and commercial beverage storage."
    },
    {
        title: "Cookware & dining ware",
        image: `${process.env.REACT_APP_BLOB_URL}/www/img/kitchens/hotel-diningware-glassware.png`,
        alt: "Table setting at a luxurious hotel restaurant with commercial-grade dinnerware and glassware."
    },

];

interface SubCategoryCarouselProps {
    variant?: "dark" | "light" | "default";
}

export default function SubCategoryCarousel({
    variant = "default",
}: SubCategoryCarouselProps) {
    const isDark = variant === "dark";

    return (
        <div className={`w-full py-12 px-4 md:px-8 lg:px-16 ${variant === "dark" ? "bg-black" : variant === "default" ? "bg-gradient-to-b from-gray-50 to-gray-100" : "bg-white"}`}>
            {/* Heading */}
            {/* <div className="mb-8 max-w-7xl mx-auto">
                <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-2 leading-tight ${isDark ? "text-white" : "text-gray-900"}`}>
                    <span className="hero-text-gradient-red">Bathroom</span> Sub-Categories
                </h2>
                <p className={`text-base md:text-lg leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Explore our full range of bathroom products and solutions.
                </p>
            </div> */}

            {/* Carousel */}
            <div className="relative w-full max-w-[1536px] mx-auto px-12">
                <Carousel
                    opts={{ loop: true, align: "center" }}
                    className="w-full"
                >
                    <CarouselContent>
                        {subCategories.map((item, index) => (
                            <CarouselItem
                                key={index}
                                className="basis-auto"
                            >
                                <div className="block group ">
                                    {/* Square Image */}
                                    <div className="w-[272px] h-[272px] mx-auto rounded-lg overflow-hidden mb-3">
                                        <img
                                            src={item.image}
                                            alt={item.alt}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.onerror = null;
                                                target.style.display = "none";
                                            }}
                                        />
                                    </div>

                                    {/* Name + Arrow below image */}
                                    <div className="flex items-center justify-between w-[272px] mx-auto px-4 sm:px-2 md:px-4 lg:px-1 xl:px-1">
                                        <span className={`text-sm font-medium transition-colors duration-200 group-hover:text-harvey-red ${isDark ? "text-white" : "text-gray-800"}`}>
                                            {item.title}
                                        </span>
                                        <ArrowRight className={`w-4 h-4 transition-all duration-200 group-hover:translate-x-1 group-hover:text-harvey-red ${isDark ? "text-gray-400" : "text-gray-500"}`} />
                                    </div>
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    <CarouselPrevious icon={ChevronLeft} variant="ghost" size="custom" className={`absolute -left-14 top-[136px] z-10 !size-12 [&_svg]:size-6 ${isDark ? "bg-white/10 hover:bg-white/20 border border-white/30 text-white hover:!text-harvey-red" : "bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:!text-harvey-red"}`} />
                    <CarouselNext icon={ChevronRight} variant="ghost" size="custom" className={`absolute -right-14 top-[136px] z-10 !size-12 [&_svg]:size-6 ${isDark ? "bg-white/10 hover:bg-white/20 border border-white/30 text-white hover:!text-harvey-red" : "bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:!text-harvey-red"}`} />
                </Carousel>
            </div>
        </div>
    );
}
